import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { DialogProvider } from "../modules/dialogs/context/DialogProvider";
import { GamesProvider } from "../modules/games/context/GamesProvider";
import { MapProvider } from "../modules/map/context/MapProvider";
import { PopUpsProvider } from "../modules/popups/context/PopUpsProvider";
import { ScoreProvider } from "../modules/score/context/ScoreProvider";
import { SfxProvider } from "../modules/sfx/context/SfxProvider";
import { UIProvider } from "../modules/ui/context/UIProvider";
import { StorageProvider } from "./StorageContext";
import { ScormAPIProvider } from "../modules/scorm/context/ScormAPIProvider";

//  TS
export type MainData = {
  name: string;
  dialogs: {};
};

export type MainAPI = {};

export type MainActions = { type: "setName"; id: number };

//  REACT
const MainDataContext = createContext<MainData>({} as MainData);
const MainAPIContext = createContext<MainAPI>({} as MainAPI);

export const MainProvider: FC<PropsWithChildren> = ({ children }) => {
  //  DATA
  const [data, dispatch] = useReducer(
    (oldData: MainData, action: MainActions): MainData => {
      switch (action.type) {
        case "setName":
          return { ...oldData, name: action.id.toString() };
      }
    },
    {} as MainData
  );

  //  API
  const api = useMemo<MainAPI>(() => {
    return { dispatch };
  }, []);

  return (
    <>
      <MainDataContext.Provider value={data}>
        <MainAPIContext.Provider value={api}>
          <StorageProvider>
            <SfxProvider>
              <ScoreProvider>
                <ScormAPIProvider>
                  <UIProvider>
                    <MapProvider>
                      <PopUpsProvider>
                        <GamesProvider>
                          <DialogProvider>{children}</DialogProvider>
                        </GamesProvider>
                      </PopUpsProvider>
                    </MapProvider>
                  </UIProvider>
                </ScormAPIProvider>
              </ScoreProvider>
            </SfxProvider>
          </StorageProvider>
        </MainAPIContext.Provider>
      </MainDataContext.Provider>
    </>
  );
};

//  CUSTOM HOOKS
export const useMainData = () => useContext(MainDataContext);
export const useMainAPI = () => useContext(MainAPIContext);
